import React from 'react';
import {
  MenuItem, ListItem, ListItemIcon, ListItemText, Menu, Box,
  alpha, useTheme as useMuiTheme
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '../../../context/ThemeContext';

/**
 * ThemeToggle - Component for switching between light, dark, and auto theme modes
 *
 * @param {string} variant - 'menuitem' for UserMenu, 'listitem' for MobileDrawer
 * @param {Function} onClose - Callback to close parent menu/drawer
 */
const ThemeToggle = ({ variant = 'menuitem', onClose }) => {
  const muiTheme = useMuiTheme();
  const { userPreference, effectiveMode, setThemeMode } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectMode = (mode) => {
    setThemeMode(mode);
    handleCloseMenu();
    if (onClose) {
      onClose();
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'light':
        return <Brightness7Icon fontSize="small" />;
      case 'dark':
        return <Brightness4Icon fontSize="small" />;
      case 'auto':
        return <BrightnessAutoIcon fontSize="small" />;
      default:
        return <BrightnessAutoIcon fontSize="small" />;
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'light':
        return 'Hell';
      case 'dark':
        return 'Dunkel';
      case 'auto':
        return 'Automatisch';
      default:
        return 'Automatisch';
    }
  };

  const modes = ['light', 'dark', 'auto'];

  // MenuItem variant for desktop UserMenu
  if (variant === 'menuitem') {
    return (
      <>
        <MenuItem onClick={handleOpenMenu} sx={{ py: 1.5 }}>
          <ListItemIcon>
            {getModeIcon(effectiveMode)}
          </ListItemIcon>
          <ListItemText
            primary="Design"
            secondary={getModeLabel(userPreference)}
          />
        </MenuItem>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 180,
              borderRadius: 2,
            },
          }}
        >
          {modes.map((mode) => (
            <MenuItem
              key={mode}
              onClick={() => handleSelectMode(mode)}
              selected={userPreference === mode}
              sx={{
                py: 1.5,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  {getModeIcon(mode)}
                </ListItemIcon>
                <ListItemText primary={getModeLabel(mode)} />
              </Box>
              {userPreference === mode && (
                <CheckIcon fontSize="small" color="primary" sx={{ ml: 2 }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // ListItem variant for mobile MobileDrawer
  if (variant === 'listitem') {
    return (
      <>
        <ListItem
          onClick={handleOpenMenu}
          sx={{
            borderRadius: 2,
            mb: 1,
            '&:hover': {
              background: alpha(muiTheme.palette.primary.main, 0.1),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: muiTheme.palette.primary.main }}>
            {getModeIcon(effectiveMode)}
          </ListItemIcon>
          <ListItemText
            primary="Design"
            secondary={getModeLabel(userPreference)}
          />
        </ListItem>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 180,
              borderRadius: 2,
            },
          }}
        >
          {modes.map((mode) => (
            <MenuItem
              key={mode}
              onClick={() => handleSelectMode(mode)}
              selected={userPreference === mode}
              sx={{
                py: 1.5,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  {getModeIcon(mode)}
                </ListItemIcon>
                <ListItemText primary={getModeLabel(mode)} />
              </Box>
              {userPreference === mode && (
                <CheckIcon fontSize="small" color="primary" sx={{ ml: 2 }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return null;
};

export default ThemeToggle;
